import { useEffect, useRef } from 'react'
import { hasExitPopupBeenSeen, hasLandingConverted } from '@utils/landingSession'

/** Tempo mínimo na página antes de poder disparar (desktop). */
const DESKTOP_ARM_MS = 6000
/** Tempo mínimo na página antes de poder disparar (mobile/tablet) — alinhado com lead qualificado (10s). */
const TOUCH_ARM_MS = 10000
/** Após armar no mobile, tempo extra antes do fallback por engagement. */
const TOUCH_ENGAGEMENT_FALLBACK_EXTRA_MS = 3000
/** Zona no topo do ecrã onde o rato conta como "a sair" (desktop). */
const EXIT_TOP_PX = 40
/** Scroll mínimo (px ou % da página) para considerar engagement no mobile. */
const SCROLL_ENGAGEMENT_RATIO = 0.2
const SCROLL_ENGAGEMENT_MIN_PX = 250
/** Scroll rápido para cima perto do topo = intenção de sair (mobile). */
const SCROLL_TOP_ZONE_PX = 50
const SCROLL_UP_MIN_DELTA = 40
const SCROLL_UP_MAX_MS = 300

type UseExitIntentOptions = {
  enabled?: boolean
  onTrigger: () => void
}

export function isMobileOrTablet(): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
    // Telefone/tablet real: pointer grosso e sem hover de rato.
    if (window.matchMedia('(pointer: coarse) and (hover: none)').matches) return true
    // iPad / tablets em landscape com rato: ecrã tátil até ~1024px.
    if (window.matchMedia('(max-width: 1024px)').matches && navigator.maxTouchPoints > 0) return true
    return false
  } catch {
    return false
  }
}

function isExitMouseEvent(e: MouseEvent): boolean {
  return e.clientY <= EXIT_TOP_PX
}

function getScrollableHeight(): number {
  return Math.max(document.documentElement.scrollHeight - window.innerHeight, 0)
}

function isScrollEngaged(maxScrollDepth: number): boolean {
  if (maxScrollDepth >= SCROLL_ENGAGEMENT_MIN_PX) return true
  const scrollable = getScrollableHeight()
  if (scrollable <= 0) return false
  return maxScrollDepth / scrollable >= SCROLL_ENGAGEMENT_RATIO
}

/**
 * Deteta intenção de saída.
 * - Desktop: após 6s, ao mover o rato para o topo / perder foco / tab hidden.
 * - Mobile/tablet: após 10s, ao voltar atrás, scroll rápido para o topo, ou engagement fallback.
 *
 * Nota: não há janela máxima — depois de "armado", fica elegível até disparar ou converter.
 */
export function useExitIntent({ enabled = true, onTrigger }: UseExitIntentOptions): void {
  const armedRef = useRef(false)
  const triggeredRef = useRef(false)
  const onTriggerRef = useRef(onTrigger)

  onTriggerRef.current = onTrigger

  useEffect(() => {
    if (!enabled) return
    if (hasExitPopupBeenSeen() || hasLandingConverted()) return

    const touch = isMobileOrTablet()
    const armMs = touch ? TOUCH_ARM_MS : DESKTOP_ARM_MS

    let maxScrollDepth = window.scrollY
    let lastScrollY = window.scrollY
    let lastScrollTime = Date.now()

    const armTimer = window.setTimeout(() => {
      armedRef.current = true
    }, armMs)

    const trigger = () => {
      if (!armedRef.current || triggeredRef.current) return
      if (hasExitPopupBeenSeen() || hasLandingConverted()) return
      triggeredRef.current = true
      onTriggerRef.current()
    }

    const onMouseOut = (e: MouseEvent) => {
      if (touch) return
      // Saiu da janela (não para um filho da página).
      if (e.relatedTarget != null) return
      if (isExitMouseEvent(e)) trigger()
    }

    const onDocumentMouseLeave = (e: MouseEvent) => {
      if (touch) return
      if (isExitMouseEvent(e)) trigger()
    }

    const onWindowBlur = () => {
      if (touch) return
      trigger()
    }

    const onVisibilityChange = () => {
      if (!document.hidden) return
      trigger()
    }

    const onPageHide = () => {
      trigger()
    }

    const onScroll = () => {
      const y = window.scrollY
      maxScrollDepth = Math.max(maxScrollDepth, y)

      if (!touch || !armedRef.current) {
        lastScrollY = y
        lastScrollTime = Date.now()
        return
      }

      const now = Date.now()
      const upwardDelta = lastScrollY - y
      const elapsed = now - lastScrollTime

      if (y <= SCROLL_TOP_ZONE_PX && upwardDelta >= SCROLL_UP_MIN_DELTA && elapsed <= SCROLL_UP_MAX_MS) {
        trigger()
      }

      lastScrollY = y
      lastScrollTime = now
    }

    let popstateHandler: (() => void) | null = null
    const backTrapTimer = window.setTimeout(() => {
      try {
        window.history.pushState({ exitIntent: true }, '')
        popstateHandler = () => trigger()
        window.addEventListener('popstate', popstateHandler, { once: true })
      } catch {
        // ignore
      }
    }, armMs)

    const engagementFallbackTimer = touch
      ? window.setTimeout(() => {
          if (isScrollEngaged(maxScrollDepth)) trigger()
        }, armMs + TOUCH_ENGAGEMENT_FALLBACK_EXTRA_MS)
      : null

    document.addEventListener('mouseout', onMouseOut)
    document.documentElement.addEventListener('mouseleave', onDocumentMouseLeave)
    window.addEventListener('blur', onWindowBlur)
    if (!touch) {
      document.addEventListener('visibilitychange', onVisibilityChange)
      window.addEventListener('pagehide', onPageHide)
    }
    if (touch) {
      window.addEventListener('scroll', onScroll, { passive: true })
    }

    return () => {
      window.clearTimeout(armTimer)
      window.clearTimeout(backTrapTimer)
      if (engagementFallbackTimer != null) window.clearTimeout(engagementFallbackTimer)
      document.removeEventListener('mouseout', onMouseOut)
      document.documentElement.removeEventListener('mouseleave', onDocumentMouseLeave)
      window.removeEventListener('blur', onWindowBlur)
      if (!touch) {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('pagehide', onPageHide)
      }
      if (touch) {
        window.removeEventListener('scroll', onScroll)
      }
      if (popstateHandler) {
        window.removeEventListener('popstate', popstateHandler)
      }
    }
  }, [enabled])
}
